import React, { useCallback, useEffect, useRef, useState } from "react";
import { Check, Loader2, AlertCircle, Lock, RotateCcw } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { LinkFieldPopover } from "@/components/inline-edit/LinkFieldPopover";
import { SelectFieldPopover } from "@/components/inline-edit/SelectFieldPopover";
import { InlineFieldInput } from "./InlineFieldInput";
import { InlineFieldTextarea } from "./InlineFieldTextarea";
import { InlineDatePicker } from "./InlineDatePicker";
import {
  type FieldSaveState,
  type InlineFieldProps,
  IDLE_FIELD_STATE,
  toCellSaveState,
} from "./types";
import type { DocFieldSchema } from "@/lib/erp/meta";
import {
  formatShopifyFieldValue,
  type FormattedValue,
} from "@/features/inventory/item-detail/components/utils/shopifyFormatting";
import {
  INLINE_DISPLAY_CLASS,
  INLINE_READONLY_CLASS,
  INLINE_SAVED_ICON_CLASS,
  INLINE_SAVING_ICON_CLASS,
  INLINE_ERROR_ICON_CLASS,
  INLINE_ERROR_RING_CLASS,
  INLINE_LOCK_ICON_CLASS,
} from "./inline-field-styles";
import { cn } from "@/lib/utils";

/** Field types that render as textarea */
const TEXTAREA_TYPES = new Set([
  "Text Editor",
  "Small Text",
  "Long Text",
  "Code",
]);

/** Field types that always fall back to modal (return null) */
const MODAL_ONLY_TYPES = new Set(["Table", "Table MultiSelect"]);

/** localStorage key for first-use hint */
const HINT_STORAGE_KEY = "inline-edit-hint-shown";

export interface InlineFieldRendererProps {
  field: DocFieldSchema;
  value: unknown;
  onCommit: (value: unknown) => void;
  onCancel: () => void;
  /** When true, renders the edit input. When false, renders display value. */
  isEditing: boolean;
  /** Called on click to enter edit mode. (Kept in API for callers; row-level click now activates.) */
  onStartEdit?: () => void;
  isReadOnly?: boolean;
  saveState?: FieldSaveState;
  placeholder?: string;
  /** Currency code for formatting (e.g., "THB") */
  currency?: string;
  /** Called on Tab/Shift+Tab to advance to next/prev field */
  onAdvance?: (direction: "next" | "prev") => void;
  /** Called to retry a failed save */
  onRetry?: () => void;
}

/**
 * Strip HTML tags for plain-text inline display.
 */
function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

/**
 * Render the display value for a field (non-editing state).
 */
function renderDisplayValue(
  field: DocFieldSchema,
  value: unknown,
  currency?: string
): React.ReactNode {
  const formatted: FormattedValue = formatShopifyFieldValue(
    field,
    value,
    currency
  );
  if (typeof formatted === "object" && "__html" in formatted) {
    const text = stripHtml(formatted.__html);
    return text || null;
  }
  if (formatted === "—") return null;
  return <>{formatted}</>;
}

/** Save state indicator icons with error tooltip */
function SaveStateIcon({
  saveState,
  onRetry,
}: {
  saveState: FieldSaveState;
  onRetry?: () => void;
}) {
  switch (saveState.status) {
    case "saved":
      return <Check className={INLINE_SAVED_ICON_CLASS} />;
    case "saving":
      return <Loader2 className={INLINE_SAVING_ICON_CLASS} />;
    case "error":
      return (
        <span className="inline-flex items-center gap-1">
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <AlertCircle className={INLINE_ERROR_ICON_CLASS} />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[240px]">
                {saveState.error || "Save failed"}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {onRetry && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRetry();
              }}
              className="p-0.5 rounded hover:bg-muted/60 transition-colors"
              aria-label="Retry save"
            >
              <RotateCcw className="h-3 w-3 text-muted-foreground hover:text-foreground" />
            </button>
          )}
        </span>
      );
    default:
      return null;
  }
}

/** First-use keyboard hint tooltip */
function KeyboardHint({
  children,
  show,
  onDismiss,
}: {
  children: React.ReactNode;
  show: boolean;
  onDismiss: () => void;
}) {
  if (!show) return <>{children}</>;

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip open onOpenChange={(open) => !open && onDismiss()}>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          Tab to next field, Esc to cancel
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Inline field renderer — dispatches by fieldtype.
 */
export function InlineFieldRenderer({
  field,
  value,
  onCommit,
  onCancel,
  isEditing,
  isReadOnly,
  saveState = IDLE_FIELD_STATE,
  placeholder,
  currency,
  onAdvance,
  onRetry,
}: InlineFieldRendererProps) {
  // First-use hint state — read localStorage once on mount
  const hintShownRef = useRef(
    typeof window !== "undefined"
      ? localStorage.getItem(HINT_STORAGE_KEY) === "1"
      : true
  );
  const [showHint, setShowHint] = useState(false);
  const hintTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const dismissHint = useCallback(() => {
    setShowHint(false);
    if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
    if (typeof window !== "undefined") {
      localStorage.setItem(HINT_STORAGE_KEY, "1");
      hintShownRef.current = true;
    }
  }, []);

  // Show hint on first edit
  useEffect(() => {
    if (isEditing && !hintShownRef.current) {
      setShowHint(true);
      // Auto-dismiss after 3s
      hintTimerRef.current = setTimeout(dismissHint, 3000);
      // Dismiss on any keydown
      const handleKey = () => dismissHint();
      document.addEventListener("keydown", handleKey, { once: true });
      return () => {
        document.removeEventListener("keydown", handleKey);
        if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
      };
    }
  }, [isEditing, dismissHint]);

  // Table / Table MultiSelect → always modal (return null, widget handles)
  if (MODAL_ONLY_TYPES.has(field.fieldtype)) {
    return null;
  }

  // Check fields: always-interactive Switch (no click-to-edit needed)
  if (field.fieldtype === "Check") {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <Switch
          checked={value === 1 || value === true}
          onCheckedChange={(checked) => onCommit(checked ? 1 : 0)}
          disabled={isReadOnly}
        />
      </div>
    );
  }

  // Read-only fields: show value with lock icon on hover
  if (isReadOnly) {
    return (
      <span className={INLINE_READONLY_CLASS}>
        {renderDisplayValue(field, value, currency) || (
          <span className="text-muted-foreground/50 italic">—</span>
        )}
        <Lock className={INLINE_LOCK_ICON_CLASS} />
      </span>
    );
  }

  // Display mode (not editing) — invisible span, row-level click handles edit activation
  if (!isEditing) {
    const hasError = saveState.status === "error";
    return (
      <span
        className={cn(INLINE_DISPLAY_CLASS, hasError && INLINE_ERROR_RING_CLASS)}
      >
        {renderDisplayValue(field, value, currency) || (
          <span className="text-muted-foreground/50 italic">
            {placeholder || `Add ${field.label?.toLowerCase() ?? "value"}`}
          </span>
        )}
        <SaveStateIcon saveState={saveState} onRetry={onRetry} />
      </span>
    );
  }

  // Edit mode — dispatch by fieldtype
  const editProps: InlineFieldProps = {
    field,
    value,
    onCommit,
    onCancel,
    placeholder,
    onAdvance,
  };

  const editElement = (() => {
    switch (field.fieldtype) {
      case "Link":
        return (
          <LinkFieldPopover
            doctype={field.options || ""}
            value={value}
            onCommit={(v) => onCommit(v)}
            onCancel={onCancel}
            saveState={toCellSaveState(saveState)}
          />
        );

      case "Select":
        return (
          <SelectFieldPopover
            options={field.options || ""}
            value={value}
            onCommit={(v) => onCommit(v)}
            onCancel={onCancel}
            saveState={toCellSaveState(saveState)}
          />
        );

      case "Date":
        return <InlineDatePicker {...editProps} />;

      default:
        if (TEXTAREA_TYPES.has(field.fieldtype)) {
          // Textarea does NOT get onAdvance — Tab→blur→commit is natural
          return (
            <InlineFieldTextarea
              field={editProps.field}
              value={editProps.value}
              onCommit={editProps.onCommit}
              onCancel={editProps.onCancel}
              placeholder={editProps.placeholder}
            />
          );
        }
        return <InlineFieldInput {...editProps} />;
    }
  })();

  return <KeyboardHint show={showHint} onDismiss={dismissHint}>{editElement}</KeyboardHint>;
}
